import { useEffect, useMemo, useState } from 'react';

import { styled, css } from 'styled-components';

import { Box, Stack, Text } from '@ssnc/bsds-core';

import workHqLogo from '../../assets/workhq-logo.svg';
import { ProfileMenu } from '../auth/ProfileMenu';
import { fetchTenants, type TenantWithLoginUrl } from '../../lib/tenants';

import { SearchInput } from './SearchInput';
import { TenantTile } from './TenantTile';

/**
 * Hand-rolled rather than @ssnc/bsds-layouts's NoContentInformation: that
 * component's styled-components reach into theme.globalSizes, a field only
 * present on the @ssnc/bsds-core version bsds-layouts bundles internally
 * (0.4.0), not the one this app depends on directly (0.8.0) and renders
 * through its own ThemeProvider — so it throws at render time. Staying on
 * bsds-core primitives alone avoids that cross-package theme mismatch.
 */
function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <Box padding={400}>
      <Stack gap={100} align="center">
        <Text type="h5">{title}</Text>
        <Text type="body">{description}</Text>
      </Stack>
    </Box>
  );
}

// bsds-core's Grid only takes a fixed column count, so tiles would squash on
// narrow windows; auto-fill lets the column count follow the viewport instead.
const TileGrid = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: ${theme.space[200]};
  `,
);

// Logo on the left, title + search stacked to its right, profile menu in the top-right corner.
// On narrow screens the title + search drop below the logo/profile row.
const Header = styled.header(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'logo profile'
      'content content';
    align-items: center;
    gap: ${theme.space[300]};

    @media (min-width: 640px) {
      grid-template-columns: auto 1fr auto;
      grid-template-areas: 'logo content profile';
      gap: ${theme.space[500]};
    }
  `,
);

const HeaderLogo = styled.div`
  grid-area: logo;
`;

const HeaderContent = styled.div`
  grid-area: content;
`;

const HeaderProfile = styled.div`
  grid-area: profile;
  align-self: start;
`;

const Logo = styled.img`
  display: block;
  width: 12rem;
  height: auto;
`;

export function TenantPicker() {
  const [tenants, setTenants] = useState<TenantWithLoginUrl[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTenants()
      .then(setTenants)
      .catch((err: Error) => setError(err.message));
  }, []);

  const filteredTenants = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return tenants ?? [];
    }

    return (tenants ?? []).filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(normalizedQuery) ||
        (tenant.subdomain ?? '').toLowerCase().includes(normalizedQuery),
    );
  }, [tenants, query]);

  let content;
  if (error) {
    content = <EmptyState title="Unable to load organizations" description={error} />;
  } else if (tenants === null) {
    content = <EmptyState title="Loading organizations…" description="This should only take a moment." />;
  } else if (filteredTenants.length === 0) {
    content = <EmptyState title="No matching organizations" description="Try a different search term." />;
  } else {
    content = (
      <TileGrid role="list">
        {filteredTenants.map((tenant) => (
          <TenantTile key={tenant.id} tenant={tenant} />
        ))}
      </TileGrid>
    );
  }

  return (
    <Stack gap={400}>
      <Header>
        <HeaderLogo>
          <Logo src={workHqLogo} alt="SS&C Blue Prism WorkHQ" />
        </HeaderLogo>
        <HeaderContent>
          <Stack gap={200}>
          <Stack gap={50}>
            <Text type="h1" styleAs="heading2">
              Choose your organization
            </Text>
            <Text type="body" themeColor="subtle">
              Select the WorkHQ tenant you want to sign in to.
            </Text>
          </Stack>
          {tenants !== null && <SearchInput value={query} onChange={setQuery} />}
          </Stack>
        </HeaderContent>
        <HeaderProfile>
          <ProfileMenu />
        </HeaderProfile>
      </Header>
      {content}
    </Stack>
  );
}
